import {
  CheckCircleFilled,
  DeleteOutlined,
  ExclamationCircleOutlined,
  ImportOutlined,
  LoadingOutlined,
  SearchOutlined,
  WarningFilled,
} from '@ant-design/icons';
import { Button, Divider, Empty, Input, Modal, Space, Table, Tag } from 'antd';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as XLSX from 'xlsx';
import { removeMember } from '../../../socket/member';

const { confirm } = Modal;

export const RemoveListMember = ({
  namespace,
  visible,
  accountByNamespace,
}) => {
  const { t } = useTranslation();
  const convertedFileRef = useRef([]);
  const [members, setMembers] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [step, setStep] = useState(1);
  const [removeStatus, setRemoveStatus] = useState({});
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    return () => {
      convertedFileRef.current = [];
      setMembers([]);
      setRemoveStatus({});
      setStep(1);
      setSearchText('');
      setPagination({
        current: 1,
        pageSize: 10,
      });
      setLoading(false);
    };
  }, [visible]);

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = async (event) => {
      const data = new Uint8Array(event.target.result);
      const workbook = XLSX.read(data, { type: 'array' });
      const worksheet = workbook.Sheets[workbook.SheetNames[0]];
      const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

      console.log('🚀 ~ handleFileUpload ~ jsonData:', jsonData);
      const parsed = jsonData
        .slice(1)
        .map(([name, phonenumber]) => ({
          name: name?.toString().trim() || '',
          phonenumber: phonenumber?.toString().trim() || '',
        }))
        .filter((item) => item.phonenumber);
      console.log('🚀 ~ handleFileUpload ~ parsed:', parsed);
      const filterInSystem =
        parsed?.filter((p) =>
          accountByNamespace?.some((a) => a.phonenumber === p.phonenumber),
        ) || [];

      convertedFileRef.current = filterInSystem;
      setMembers(filterInSystem);
      setRemoveStatus({});
    };

    reader.readAsArrayBuffer(file);
    e.target.value = '';
    setStep(2);
  };

  const handleDelete = (id) => {
    const updated = members.filter((m) => m.id !== id);
    convertedFileRef.current = updated;
    setMembers(updated);
    setRemoveStatus((prev) => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
  };

  const filteredMembers = members.filter((m) => {
    const q = searchText.toLowerCase();

    return (
      String(m.name || '')
        .toLowerCase()
        .includes(q) ||
      String(m.phonenumber || '')
        .toLowerCase()
        .includes(q)
    );
  });

  const handleRemoveMembers = async () => {
    try {
      setLoading(true);

      const initialStatus = members.reduce((acc, m) => {
        acc[m.id] = 'pending';
        return acc;
      }, {});

      setRemoveStatus(initialStatus);

      for (const member of members) {
        const data = {
          username: member?.username || member?.phonenumber,
          phonenumber: member?.phonenumber,
          namespace,
        };

        try {
          await removeMember(data);

          setRemoveStatus((prev) => ({
            ...prev,
            [member.id]: 'success',
          }));
        } catch (err) {
          setRemoveStatus((prev) => ({
            ...prev,
            [member.id]: {
              status: 'error',
              error: err?.message,
            },
          }));
        }
      }
    } finally {
      setLoading(false);
    }
  };

  const onRemoveMembers = useCallback(() => {
    confirm({
      title: `${t('Remove all members')}?`,
      icon: <ExclamationCircleOutlined />,
      okText: t('Confirm'),

      onOk() {
        handleRemoveMembers();
      },

      onCancel() {},
    });
  }, [namespace, members, t]);

  const renderRemoveStatus = (id) => {
    const status = removeStatus[id];

    if (!status) return null;

    const commonStyle = {
      display: 'flex',
      alignItems: 'center',
      gap: 4,
      fontWeight: 500,
    };

    if (status === 'pending') {
      return (
        <span style={{ ...commonStyle, color: '#1677ff' }}>
          <LoadingOutlined spin />
          {t('Processing')}
        </span>
      );
    }

    if (status === 'success') {
      return (
        <span style={{ ...commonStyle, color: '#009741' }}>
          <CheckCircleFilled className="icon-done" />
          {t('Success')}
        </span>
      );
    }

    if (typeof status === 'object' && status.status === 'error') {
      return (
        <span style={{ ...commonStyle, color: '#b39d00' }}>
          <WarningFilled className="icon-error" />
          <span>{status?.error}</span>
        </span>
      );
    }

    return null;
  };

  const columns = [
    {
      title: t('Name'),
      dataIndex: 'name',
      key: 'name',
      render: (_, record) =>
        record?.name
          ? record.name
          : `${record.firstName || ''} ${record.lastName || ''}`,
    },
    {
      title: t('Phone number'),
      dataIndex: 'phonenumber',
      key: 'phonenumber',
    },
    {
      title: t('Result'),
      key: 'removeStatus',
      render: (_, record) => renderRemoveStatus(record.id),
    },
    {
      title: t('Action'),
      key: 'action',
      render: (_, record) => (
        <Button
          type="text"
          danger
          icon={<DeleteOutlined />}
          size="small"
          onClick={() => handleDelete(record.id)}
        />
      ),
    },
  ];

  return (
    <div className="remove-list-member">
      {step === 1 && (
        <>
          <div
            style={{
              fontSize: 12,
              color: '#ffffffa6',
              textAlign: 'center',
              margin: '10px 0',
            }}
          >
            <a
              style={{ textDecoration: 'underline' }}
              href="https://db1.beex.vn/template_remove_list_member.xlsx"
            >
              {t('Download the template')}
            </a>
            &nbsp;
            {t(
              'and fill in the member information. Then import the file to remove members.',
            )}
          </div>

          <input
            type="file"
            accept=".xlsx"
            onChange={handleFileUpload}
            style={{ display: 'none' }}
            id="fileUpload"
          />

          <div className="btn-import">
            <Button
              icon={<ImportOutlined />}
              type="primary"
              onClick={() => document.getElementById('fileUpload').click()}
            >
              {t('Import list member')}
            </Button>
          </div>
        </>
      )}
      {step === 2 && (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'start',
            justifyContent: 'center',
            gap: 10,
          }}
        >
          {members.length > 0 ? (
            <>
              <Input
                allowClear
                placeholder={t('Search member...')}
                prefix={<SearchOutlined />}
                style={{ width: 280 }}
                value={searchText}
                onChange={(e) => {
                  setSearchText(e.target.value);

                  setPagination((prev) => ({
                    ...prev,
                    current: 1,
                  }));
                }}
              />
              <Table
                dataSource={filteredMembers}
                columns={columns}
                rowKey="id"
                size="small"
                pagination={{
                  ...pagination,
                  showSizeChanger: true,
                }}
                onChange={(pager) => {
                  setPagination({
                    current: pager.current,
                    pageSize: pager.pageSize,
                  });
                }}
              />
            </>
          ) : (
            <div
              style={{
                width: '100%',
                display: 'flex',
                justifyContent: 'center',
                padding: '24px 0',
              }}
            >
              <Empty
                description={t(
                  'No imported members match any existing members in the current namespace.',
                )}
              />
            </div>
          )}
        </div>
      )}

      {step === 2 && (
        <>
          <Divider />
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <>
              <Button
                type="default"
                onClick={() => {
                  setStep(1);
                  convertedFileRef.current = [];
                  setMembers([]);
                  setRemoveStatus({});
                }}
              >
                {t('Back')}
              </Button>
              {members.length > 0 && (
                <Button
                  type="primary"
                  disabled={members.length === 0}
                  onClick={onRemoveMembers}
                  loading={loading}
                >
                  {t('Remove')}
                </Button>
              )}
            </>
          </div>
        </>
      )}
    </div>
  );
};
